/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';
import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

export const ProductStatus = Object.freeze({
  DRAFT: 'draft',
  ACTIVE: 'active',
  RETIRED: 'retired',
});

const productStatusValues = Object.values(ProductStatus);
const skuCodeRegex = /^[A-Z0-9][A-Z0-9._-]*$/;

export const productSchema = Yup.object().shape({
  sku: Yup.string()
    .trim()
    .matches(skuCodeRegex, 'SKU must use only uppercase letters, numbers, periods, underscores, or hyphens')
    .required('SKU is required'),
  name: Yup.string().trim().required('Product name is required'),
  description: Yup.string().nullable(),
  status: Yup.string().oneOf(productStatusValues).default(ProductStatus.DRAFT).required('Product status is required'),
  activeVersionId: Yup.string().nullable(),
  // Printed product label QR content: the customer-facing link (e.g. a
  // quick-start guide) and the short header shown above it on the label.
  labelQrHeader: Yup.string().trim().nullable(),
  labelQrUrl: Yup.string().trim().url('Label QR URL must be a valid URL').nullable(),
  createdAt: YupFirestoreTimestamp.nullable(),
  updatedAt: YupFirestoreTimestamp.nullable(),
});

/**
 * @typedef {Yup.InferType<typeof productSchema>} ProductSchema
 * @typedef {BaseDocData & ProductSchema} ProductDocData
 */

export default class Product {
  /** @type {ProductDocData['validation']} */ validation = 'passed';
  /** @type {ProductDocData['id']} */ id;
  /** @type {ProductDocData['sku']} */ sku;
  /** @type {ProductDocData['name']} */ name;
  /** @type {ProductDocData['description']} */ description;
  /** @type {ProductDocData['status']} */ status;
  /** @type {ProductDocData['activeVersionId']} */ activeVersionId;
  /** @type {ProductDocData['labelQrHeader']} */ labelQrHeader;
  /** @type {ProductDocData['labelQrUrl']} */ labelQrUrl;
  /** @type {ProductDocData['createdAt']} */ createdAt;
  /** @type {ProductDocData['updatedAt']} */ updatedAt;

  /**
   * @param {ProductDocData} param0 Raw Firestore product document.
   */
  constructor({
    id,
    sku,
    name,
    description = null,
    status = ProductStatus.DRAFT,
    activeVersionId = null,
    labelQrHeader = null,
    labelQrUrl = null,
    createdAt,
    updatedAt,
  }) {
    const validatedData = productSchema.validateSync({
      sku,
      name,
      description,
      status,
      activeVersionId,
      labelQrHeader,
      labelQrUrl,
      createdAt,
      updatedAt,
    });

    this.id = id || null;
    this.sku = validatedData.sku;
    this.name = validatedData.name;
    this.description = validatedData.description || null;
    this.status = validatedData.status;
    this.activeVersionId = validatedData.activeVersionId || null;
    this.labelQrHeader = validatedData.labelQrHeader || null;
    this.labelQrUrl = validatedData.labelQrUrl || null;
    this.createdAt = validatedData.createdAt;
    this.updatedAt = validatedData.updatedAt;
  }

  /**
   * @returns {Omit<ProductDocData, 'id'>}
   */
  toFirestore({ serverTimestamp }) {
    return {
      sku: this.sku,
      name: this.name,
      description: this.description || null,
      status: this.status,
      activeVersionId: this.activeVersionId || null,
      labelQrHeader: this.labelQrHeader || null,
      labelQrUrl: this.labelQrUrl || null,
      createdAt: this.createdAt || serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
  }

  static fromFirestore(docData) {
    return new Product(docData);
  }
}
